import { ArrowRight, Pencil, ArrowLeft, Users, BarChart3, List, TrendingUp, PieChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { AnalyticsQuery, Filter } from '../types/analytics.types';
import { getFieldConfig, OPERATOR_LABELS } from '../lib/filterConfig';
import { useAnalyticsStore } from '../stores/analyticsStore';
import { useCatalogStore } from '../stores/catalogStore';
import { useExecuteQuery, usePreviewCount } from '../hooks/useAnalyticsQuery';
import { formatNumber } from '../lib/formatters';

interface QueryConfirmationProps {
  query: AnalyticsQuery;
}

export function QueryConfirmation({ query }: QueryConfirmationProps) {
  const { setUIState, loadQueryIntoBuilder, error, previewCount } = useAnalyticsStore();
  const executeQuery = useExecuteQuery();
  const { groupBy: groupByOptions, trendMetrics } = useCatalogStore();

  usePreviewCount(query.filters, true);

  const handleEdit = () => {
    loadQueryIntoBuilder(query);
    setUIState('idle');
  };

  const handleBack = () => {
    setUIState('idle');
  };

  const handleRun = () => {
    executeQuery.mutate({ query });
  };

  const formatFilterNaturally = (filter: Filter): string => {
    const fieldConfig = getFieldConfig(filter.category, filter.field);
    if (!fieldConfig) return '';

    const fieldLabel = fieldConfig.label;
    const operator = filter.operator;
    const value = filter.value;

    if (operator === 'is_true') {
      if (fieldConfig.valueType === 'boolean') {
        return `has ${fieldLabel.toLowerCase()}`;
      }
      return `${fieldLabel} is yes`;
    }
    if (operator === 'is_false') {
      if (fieldConfig.valueType === 'boolean') {
        return `does not have ${fieldLabel.toLowerCase()}`;
      }
      return `${fieldLabel} is no`;
    }

    if (operator === 'between' && typeof value === 'object' && value !== null) {
      const range = value as { from: number | string; to: number | string };
      const unit = fieldConfig.unit ? ` ${fieldConfig.unit}` : '';
      return `${fieldLabel} is between ${range.from}${unit} and ${range.to}${unit}`;
    }

    if (operator === 'within_last_days') {
      return `${fieldLabel} within the last ${value} days`;
    }

    if (operator === 'more_than_days_ago') {
      return `${fieldLabel} more than ${value} days ago`;
    }

    if (operator === 'greater_than') {
      const unit = fieldConfig.unit ? ` ${fieldConfig.unit}` : '';
      return `${fieldLabel} is ${value}${unit} or higher`;
    }

    if (operator === 'greater_than_or_equals') {
      const unit = fieldConfig.unit ? ` ${fieldConfig.unit}` : '';
      return `${fieldLabel} is at least ${value}${unit}`;
    }

    if (operator === 'less_than') {
      const unit = fieldConfig.unit ? ` ${fieldConfig.unit}` : '';
      return `${fieldLabel} is under ${value}${unit}`;
    }

    if (operator === 'less_than_or_equals') {
      const unit = fieldConfig.unit ? ` ${fieldConfig.unit}` : '';
      return `${fieldLabel} is at most ${value}${unit}`;
    }

    if (Array.isArray(value)) {
      const labels = value.map((v) => {
        const option = fieldConfig.options?.find((o) => o.value === v);
        return option?.label || v;
      });
      if (operator === 'in') {
        return `${fieldLabel} is ${labels.join(' or ')}`;
      }
      if (operator === 'not_in') {
        return `${fieldLabel} is not ${labels.join(' or ')}`;
      }
      return `${fieldLabel} ${OPERATOR_LABELS[operator]} ${labels.join(', ')}`;
    }

    if (fieldConfig.options) {
      const option = fieldConfig.options.find((o) => o.value === value);
      const displayValue = option?.label || String(value);
      if (operator === 'equals') {
        return `${fieldLabel} is ${displayValue}`;
      }
      return `${fieldLabel} ${OPERATOR_LABELS[operator]} ${displayValue}`;
    }

    const unit = fieldConfig.unit ? ` ${fieldConfig.unit}` : '';
    if (operator === 'equals') {
      return `${fieldLabel} is ${value}${unit}`;
    }

    return `${fieldLabel} ${OPERATOR_LABELS[operator]} ${value}${unit}`;
  };

  const getOutputDescription = () => {
    const { output } = query;
    switch (output.type) {
      case 'count':
        return { icon: Users, text: 'Count total patients' };
      case 'list':
        return { icon: List, text: 'Show patient list' };
      case 'trend': {
        const metric = trendMetrics.find((m) => m.value === (output.metric || 'consents_obtained'))?.label ?? 'Consents obtained';
        return {
          icon: TrendingUp,
          text: `${metric} over time, by ${output.timeRange?.granularity || 'month'}`,
        };
      }
      case 'breakdown': {
        const groupByLabel =
          groupByOptions.find((g) => g.value === output.groupBy)?.label ?? output.groupBy?.replace(/_/g, ' ') ?? 'category';
        return { icon: BarChart3, text: `Break down by ${groupByLabel.toLowerCase()}` };
      }
      default:
        return { icon: PieChart, text: 'Show results' };
    }
  };

  const outputInfo = getOutputDescription();
  const OutputIcon = outputInfo.icon;

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Here's what I understood:</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Let me know if this looks right
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Start over
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            Find patients where:
          </h3>
          {query.filters.length === 0 ? (
            <p className="text-sm">All patients (no filters applied)</p>
          ) : (
            <ul className="space-y-2">
              {query.filters.map((filter, index) => (
                <li key={filter.id || index} className="flex items-start gap-2 text-sm">
                  <span className="text-primary mt-0.5">•</span>
                  <span>{formatFilterNaturally(filter)}</span>
                  {index < query.filters.length - 1 && (
                    <span className="text-muted-foreground ml-1">
                      ({query.filterLogic})
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <OutputIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Then:</span>
          <span>{outputInfo.text}</span>
        </div>

        {previewCount !== null && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <span className="text-lg">
                <span className="font-semibold">{formatNumber(previewCount)}</span>
                {' '}patients match{previewCount === 1 ? 'es' : ''} this criteria
              </span>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-wrap justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={handleEdit}>
          <Pencil className="h-4 w-4 mr-2" />
          Adjust
        </Button>
        <Button onClick={handleRun} disabled={executeQuery.isPending}>
          {executeQuery.isPending ? (
            'Finding patients...'
          ) : (
            <>
              Show Results
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
